import { computed } from "vue";
import { useNuxtApp, useRequestURL, useRuntimeConfig, useState } from "#app";
import type { OrganizationPublicPreferencesResponse } from "~/types/organizations";
import { getApiErrorStatus } from "~/utils/apiError";
import { isPlatformHost } from "~/utils/loginHost";

type DomainBrandStatus = "idle" | "loading" | "ready" | "unavailable" | "error";
interface DomainBrandState {
  preferences: OrganizationPublicPreferencesResponse | null;
  status: DomainBrandStatus;
}
interface DomainBrandRequest {
  generation: number;
  pending?: Promise<void>;
}

// Requests belong to a Nuxt app, not a module-global user or school session.
const requests = new WeakMap<object, Map<string, DomainBrandRequest>>();

/** Public domain identity survives login/logout, but never persists on disk. */
export const useSchoolDomainBrand = () => {
  const app = useNuxtApp();
  const hostname = useRequestURL().hostname;
  const config = useRuntimeConfig();
  const platformHost = isPlatformHost(
    hostname,
    config.public.ORIGIN_URL as string | undefined,
  );
  const state = useState<DomainBrandState>(
    `school-domain-brand:${hostname}`,
    () => ({
      preferences: null,
      status: "idle",
    }),
  );
  let appRequests = requests.get(app);
  if (!appRequests) {
    appRequests = new Map();
    requests.set(app, appRequests);
  }
  let request = appRequests.get(hostname);
  if (!request) {
    request = { generation: 0 };
    appRequests.set(hostname, request);
  }
  const currentRequest = request;

  const resolve = (refresh = false): Promise<void> => {
    if (!refresh && currentRequest.pending) return currentRequest.pending;
    if (!refresh && state.value.status !== "idle") return Promise.resolve();
    const generation = ++currentRequest.generation;
    state.value = { preferences: null, status: "loading" };
    const pending = (async () => {
      try {
        const preferences =
          await app.$api.organizations.getPublicPreferencesByDomain();
        if (generation !== currentRequest.generation) return;
        state.value = preferences.white_label_enabled
          ? { preferences, status: "ready" }
          : { preferences: null, status: "unavailable" };
      } catch (error) {
        if (generation !== currentRequest.generation) return;
        state.value = {
          preferences: null,
          status: getApiErrorStatus(error) === 404 ? "unavailable" : "error",
        };
      } finally {
        if (generation === currentRequest.generation)
          currentRequest.pending = undefined;
      }
    })();
    currentRequest.pending = pending;
    return pending;
  };

  const refreshForOrganization = async (
    organizationId: number,
    customDomain?: string | null,
  ) => {
    if (platformHost) return;
    await resolve();
    if (
      state.value.preferences?.organization_id !== organizationId &&
      customDomain !== hostname
    )
      return;
    await resolve(true);
  };

  return {
    platformHost,
    preferences: computed(() => state.value.preferences),
    status: computed(() => state.value.status),
    resolving: computed(() => ["idle", "loading"].includes(state.value.status)),
    resolve,
    retry: () => resolve(true),
    refreshForOrganization,
  };
};
